import { type LibraryCard } from '@shared/domain';

export interface SopEntry {
  id: string;
  title: string;
  category: 'safety' | 'procedure' | 'equipment' | 'customer' | 'emergency';
  steps: string[];
  tags: string[];
  relatedCards: string[];
  lastAccessed?: string | undefined;
  accessCount: number;
}

export interface Flashcard {
  id: string;
  question: string;
  answer: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  lastReviewed?: string | undefined;
  nextReview?: string | undefined;
  streak: number;
  timesCorrect: number;
  timesIncorrect: number;
  sourceType?: 'card' | 'glossary' | 'sop' | undefined;
  sourceId?: string | undefined;
}

export interface FlashcardSession {
  cardsReviewed: number;
  correctCount: number;
  duration: number;
  date: string;
}

export interface PinboardEntry {
  cardId: string;
  pinnedAt: string;
  order: number;
  note?: string | undefined;
  kind: 'card' | 'sop' | 'glossary';
  title: string;
}

export interface GlossaryEntry {
  term: string;
  definition: string;
  abbreviation?: string | undefined;
  relatedTerms: string[];
  category: string;
  nfpaReference?: string | undefined;
}

export interface FavoriteSet {
  id: string;
  name: string;
  description?: string | undefined;
  cardIds: string[];
  createdAt: string;
  updatedAt: string;
  color?: string | undefined;
}

export interface FavoriteSetConflict {
  incoming: FavoriteSet;
  existing: FavoriteSet;
  reason: 'id' | 'name';
}

export interface FavoriteSetImportPreview {
  incoming: FavoriteSet[];
  conflicts: FavoriteSetConflict[];
  missingCardIds: string[];
  warnings: string[];
}

export type FavoriteSetConflictChoice = 'rename' | 'replace' | 'skip';

const STORAGE_KEYS = {
  sops: 'sc-pocket-sops-v1',
  flashcards: 'sc-flashcards-v1',
  flashcardSessions: 'sc-flashcard-sessions-v1',
  pinboard: 'sc-pinboard-v1',
  glossary: 'sc-glossary-v1',
  favoriteSets: 'sc-favorite-sets-v1',
  libraryRecentSearches: 'sc-library-recent-searches-v1',
  spotlightRecentSearches: 'sc-spotlight-recent-searches-v1',
  favoriteTopics: 'sc-library-favorite-topics-v1',
} as const;

const KNOWLEDGE_EVENT = 'service-companion:knowledge-change';
const MAX_RECENTS = 10;
const MAX_PINS = 25;

const SOP_SEEDS: SopEntry[] = [
  {
    id: 'sop-odor-arrival',
    title: 'Odor call arrival sweep',
    category: 'emergency',
    steps: [
      'Pause outside and confirm what the customer observed before entering the structure.',
      'Start at the meter and entry path so your first reading has a clean baseline.',
      'Control ignition sources and ventilation before working deeper into the space.',
      'Document the release, isolation, or escalation decision before leaving the address.',
    ],
    tags: ['odor', 'emergency', 'arrival'],
    relatedCards: ['card-odor-classification', 'card-leak-test-procedure'],
    accessCount: 0,
  },
  {
    id: 'sop-manifold-verify',
    title: 'Manifold and meter cross-check',
    category: 'procedure',
    steps: [
      'Match the work order, meter number, and manifold notes before touching valves.',
      'Trace the branch line visually from appliance side to meter side.',
      'Confirm disconnected outlets are capped, tagged, and reflected in notes.',
      'Write down any mismatch before moving into purge or relight work.',
    ],
    tags: ['manifold', 'meter', 'verification'],
    relatedCards: ['card-meter-sizing', 'card-regulator-vent'],
    accessCount: 0,
  },
  {
    id: 'sop-customer-walkthrough',
    title: 'Customer handoff before leaving',
    category: 'customer',
    steps: [
      'Explain what was checked, restored, or left shut off in plain language.',
      'Call out any appliance or vent issue that still needs follow-up.',
      'Make sure the customer knows where the shutoff is and when to call back.',
      'Leave one clear next step instead of a long list of maybes.',
    ],
    tags: ['customer', 'handoff', 'closeout'],
    relatedCards: ['card-relight-sequence', 'card-vent-termination'],
    accessCount: 0,
  },
  {
    id: 'sop-cgi-setup',
    title: 'CGI setup and fresh-air check',
    category: 'equipment',
    steps: [
      'Confirm battery status and fresh-air calibration before entering the survey path.',
      'Inspect probe, filter, and hose before taking the first reading.',
      'Move from clean air toward the suspect area to keep readings interpretable.',
      'Record the highest reading and exact location in the notes.',
    ],
    tags: ['cgi', 'equipment', 'survey'],
    relatedCards: ['card-odor-classification', 'card-leak-test-procedure'],
    accessCount: 0,
  },
];

const FLASHCARD_SEEDS: Flashcard[] = [
  {
    id: 'flashcard-cgi',
    question: 'What should you confirm before trusting a CGI reading?',
    answer: 'Fresh-air calibration, probe condition, and where the clean-air baseline was taken.',
    category: 'Safety',
    difficulty: 'medium',
    streak: 0,
    timesCorrect: 0,
    timesIncorrect: 0,
  },
  {
    id: 'flashcard-sediment-trap',
    question: 'What is the quick field purpose of a sediment trap reminder?',
    answer: 'It helps you catch debris-control issues before appliance startup or relight.',
    category: 'Appliances',
    difficulty: 'easy',
    streak: 0,
    timesCorrect: 0,
    timesIncorrect: 0,
  },
  {
    id: 'flashcard-csst',
    question: 'Why does CSST bonding matter on service work?',
    answer: 'Because missing or damaged bonding can turn a routine visit into a repeat safety problem.',
    category: 'Piping',
    difficulty: 'hard',
    streak: 0,
    timesCorrect: 0,
    timesIncorrect: 0,
  },
];

const GLOSSARY_SEEDS: GlossaryEntry[] = [
  {
    term: 'CGI',
    abbreviation: 'CGI',
    definition: 'Combustible gas indicator used to survey for gas presence and document readings.',
    relatedTerms: ['LEL', 'Meter survey'],
    category: 'Equipment',
  },
  {
    term: 'CSST',
    abbreviation: 'CSST',
    definition: 'Corrugated stainless steel tubing used for gas piping where allowed and properly bonded.',
    relatedTerms: ['Bonding', 'Piping'],
    category: 'Equipment',
  },
  {
    term: 'Sediment trap',
    definition: 'A debris-catching fitting arrangement installed ahead of some appliances to protect controls.',
    relatedTerms: ['Drip leg', 'Appliance connector'],
    category: 'Procedure',
    nfpaReference: '54-9.6',
  },
  {
    term: 'Regulator',
    definition: 'A device that reduces inlet pressure to a controlled outlet pressure for downstream piping or equipment.',
    relatedTerms: ['Lock-up', 'Meter set'],
    category: 'Equipment',
  },
  {
    term: 'Manifold',
    definition: 'A branch assembly that feeds multiple appliance drops or outlet points from a common section of piping.',
    relatedTerms: ['Cross-meter', 'Branch line'],
    category: 'Procedure',
  },
  {
    term: 'LEL',
    abbreviation: 'LEL',
    definition: 'Lower explosive limit, the concentration where gas can begin to ignite in air.',
    relatedTerms: ['CGI', 'Ventilate'],
    category: 'NFPA',
  },
  {
    term: 'Bonding',
    definition: 'The electrical connection used to minimize voltage differences that can damage gas piping systems such as CSST.',
    relatedTerms: ['CSST', 'Grounding'],
    category: 'NFPA',
  },
];

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function emitKnowledgeChange(): void {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent(KNOWLEDGE_EVENT));
}

export function subscribeKnowledgeChanges(callback: () => void): () => void {
  if (!isBrowser()) return () => undefined;
  const handler = () => callback();
  window.addEventListener(KNOWLEDGE_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(KNOWLEDGE_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

function readJson<T>(key: string, fallback: T): T {
  if (!isBrowser()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (!isBrowser()) return;
  window.localStorage.setItem(key, JSON.stringify(value));
  emitKnowledgeChange();
}

function readStringArray(key: string): string[] {
  const parsed = readJson<unknown>(key, []);
  return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
}

function saveStringArray(key: string, values: string[], limit = MAX_RECENTS): void {
  writeJson(key, Array.from(new Set(values)).slice(0, limit));
}

function normalizeFavoriteSet(set: FavoriteSet): FavoriteSet {
  return {
    ...set,
    name: set.name.trim(),
    description: set.description?.trim() || undefined,
    cardIds: Array.from(new Set(set.cardIds.filter(Boolean))),
    createdAt: set.createdAt || new Date().toISOString(),
    updatedAt: set.updatedAt || new Date().toISOString(),
  };
}

export function getPocketSops(): SopEntry[] {
  const entries = readJson<SopEntry[]>(STORAGE_KEYS.sops, SOP_SEEDS);
  if (entries.length === 0) return SOP_SEEDS;
  return entries;
}

export function savePocketSop(entry: SopEntry): SopEntry {
  const entries = getPocketSops();
  const next = {
    ...entry,
    steps: entry.steps.map((step) => step.trim()).filter(Boolean),
    tags: Array.from(new Set(entry.tags.map((tag) => tag.trim()).filter(Boolean))),
    relatedCards: Array.from(new Set(entry.relatedCards)),
    accessCount: entry.accessCount || 0,
  };
  const index = entries.findIndex((current) => current.id === next.id);
  if (index >= 0) {
    entries[index] = next;
  } else {
    entries.unshift(next);
  }
  writeJson(STORAGE_KEYS.sops, entries);
  return next;
}

export function deletePocketSop(id: string): boolean {
  const entries = getPocketSops();
  const next = entries.filter((entry) => entry.id !== id);
  if (next.length === entries.length) return false;
  writeJson(STORAGE_KEYS.sops, next);
  return true;
}

export function recordPocketSopAccess(id: string): void {
  const entries = getPocketSops();
  const target = entries.find((entry) => entry.id === id);
  if (!target) return;
  target.accessCount += 1;
  target.lastAccessed = new Date().toISOString();
  writeJson(STORAGE_KEYS.sops, entries);
}

export function getFlashcards(): Flashcard[] {
  const cards = readJson<Flashcard[]>(STORAGE_KEYS.flashcards, FLASHCARD_SEEDS);
  return cards.length === 0 ? FLASHCARD_SEEDS : cards;
}

export function getFlashcardCategories(): string[] {
  return Array.from(new Set(getFlashcards().map((card) => card.category))).sort((left, right) => left.localeCompare(right));
}

export function getDueFlashcards(category?: string): Flashcard[] {
  const now = new Date().toISOString();
  return getFlashcards()
    .filter((card) => !category || card.category === category)
    .filter((card) => !card.nextReview || card.nextReview <= now)
    .sort((left, right) => (left.lastReviewed || '').localeCompare(right.lastReviewed || ''));
}

export function saveFlashcard(card: Flashcard): Flashcard {
  const cards = getFlashcards();
  const next = {
    ...card,
    question: card.question.trim(),
    answer: card.answer.trim(),
    category: card.category.trim() || 'General',
  };
  const index = cards.findIndex((entry) => entry.id === next.id);
  if (index >= 0) {
    cards[index] = next;
  } else {
    cards.unshift(next);
  }
  writeJson(STORAGE_KEYS.flashcards, cards);
  return next;
}

export function createFlashcard(input: Omit<Flashcard, 'id' | 'streak' | 'timesCorrect' | 'timesIncorrect'>): Flashcard {
  return saveFlashcard({
    ...input,
    id: `flashcard-${Date.now()}`,
    streak: 0,
    timesCorrect: 0,
    timesIncorrect: 0,
  });
}

function getDifficultyDays(difficulty: Flashcard['difficulty'], streak: number): number {
  const base = difficulty === 'easy' ? 4 : difficulty === 'medium' ? 2 : 1;
  return Math.max(1, base * Math.max(1, streak));
}

export function reviewFlashcard(cardId: string, correct: boolean): Flashcard | null {
  const cards = getFlashcards();
  const target = cards.find((card) => card.id === cardId);
  if (!target) return null;
  const now = new Date();
  target.lastReviewed = now.toISOString();
  if (correct) {
    target.streak += 1;
    target.timesCorrect += 1;
    const next = new Date(now);
    next.setDate(next.getDate() + getDifficultyDays(target.difficulty, target.streak));
    target.nextReview = next.toISOString();
  } else {
    target.streak = 0;
    target.timesIncorrect += 1;
    const next = new Date(now);
    next.setDate(next.getDate() + 1);
    target.nextReview = next.toISOString();
  }
  writeJson(STORAGE_KEYS.flashcards, cards);
  return target;
}

export function deleteFlashcard(id: string): boolean {
  const cards = getFlashcards();
  const next = cards.filter((card) => card.id !== id);
  if (next.length === cards.length) return false;
  writeJson(STORAGE_KEYS.flashcards, next);
  return true;
}

export function getFlashcardSessions(): FlashcardSession[] {
  return readJson<FlashcardSession[]>(STORAGE_KEYS.flashcardSessions, []);
}

export function recordFlashcardSession(session: FlashcardSession): void {
  const sessions = getFlashcardSessions();
  sessions.push(session);
  writeJson(STORAGE_KEYS.flashcardSessions, sessions.slice(-120));
}

export function getFlashcardDailyStreak(): number {
  const days = Array.from(new Set(getFlashcardSessions().map((session) => session.date))).sort();
  if (days.length === 0) return 0;
  let streak = 0;
  const cursor = new Date();
  for (let index = days.length - 1; index >= 0; index -= 1) {
    const expected = cursor.toISOString().slice(0, 10);
    if (days[index] !== expected) break;
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export function getPinboard(): PinboardEntry[] {
  return readJson<PinboardEntry[]>(STORAGE_KEYS.pinboard, []).sort((left, right) => left.order - right.order);
}

export function togglePinboardEntry(entry: Omit<PinboardEntry, 'pinnedAt' | 'order'>): boolean {
  const pins = getPinboard();
  const existingIndex = pins.findIndex((pin) => pin.cardId === entry.cardId && pin.kind === entry.kind);
  if (existingIndex >= 0) {
    pins.splice(existingIndex, 1);
    writeJson(
      STORAGE_KEYS.pinboard,
      pins.map((pin, index) => ({ ...pin, order: index })),
    );
    return false;
  }
  if (pins.length >= MAX_PINS) return false;
  pins.push({
    ...entry,
    pinnedAt: new Date().toISOString(),
    order: pins.length,
  });
  writeJson(STORAGE_KEYS.pinboard, pins);
  return true;
}

export function updatePinboardNote(cardId: string, kind: PinboardEntry['kind'], note: string): void {
  const pins = getPinboard();
  const target = pins.find((pin) => pin.cardId === cardId && pin.kind === kind);
  if (!target) return;
  target.note = note.trim() || undefined;
  writeJson(STORAGE_KEYS.pinboard, pins);
}

export function reorderPinboard(cardIds: string[]): void {
  const pins = getPinboard();
  const orderMap = new Map(cardIds.map((id, index) => [id, index]));
  const next = [...pins].sort((left, right) => {
    const leftOrder = orderMap.get(left.cardId) ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = orderMap.get(right.cardId) ?? Number.MAX_SAFE_INTEGER;
    return leftOrder - rightOrder;
  });
  writeJson(
    STORAGE_KEYS.pinboard,
    next.map((pin, index) => ({ ...pin, order: index })),
  );
}

export function getPinboardMeta(): { count: number; max: number; warning: boolean } {
  const count = getPinboard().length;
  return { count, max: MAX_PINS, warning: count >= 20 };
}

export function getGlossaryEntries(): GlossaryEntry[] {
  const entries = readJson<GlossaryEntry[]>(STORAGE_KEYS.glossary, GLOSSARY_SEEDS);
  return entries.length === 0 ? GLOSSARY_SEEDS : entries;
}

export function saveGlossaryEntry(entry: GlossaryEntry): GlossaryEntry {
  const entries = getGlossaryEntries();
  const index = entries.findIndex((current) => current.term.toLowerCase() === entry.term.toLowerCase());
  if (index >= 0) {
    entries[index] = entry;
  } else {
    entries.push(entry);
  }
  writeJson(STORAGE_KEYS.glossary, entries);
  return entry;
}

export function deleteGlossaryEntry(term: string): boolean {
  const entries = getGlossaryEntries();
  const next = entries.filter((entry) => entry.term.toLowerCase() !== term.toLowerCase());
  if (next.length === entries.length) return false;
  writeJson(STORAGE_KEYS.glossary, next);
  return true;
}

export function createFlashcardFromGlossary(entry: GlossaryEntry): Flashcard {
  return createFlashcard({
    question: `What does "${entry.term}" mean in the field?`,
    answer: entry.definition,
    category: entry.category,
    difficulty: 'easy',
    sourceType: 'glossary',
    sourceId: entry.term,
  });
}

export function getFavoriteSets(): FavoriteSet[] {
  return readJson<FavoriteSet[]>(STORAGE_KEYS.favoriteSets, []).map(normalizeFavoriteSet);
}

export function saveFavoriteSet(set: FavoriteSet): FavoriteSet {
  const sets = getFavoriteSets();
  const normalized = normalizeFavoriteSet(set);
  const index = sets.findIndex((entry) => entry.id === normalized.id);
  if (index >= 0) {
    sets[index] = normalized;
  } else {
    sets.unshift(normalized);
  }
  writeJson(STORAGE_KEYS.favoriteSets, sets);
  return normalized;
}

export function createFavoriteSet(input: Omit<FavoriteSet, 'id' | 'createdAt' | 'updatedAt'>): FavoriteSet {
  const now = new Date().toISOString();
  return saveFavoriteSet({
    ...input,
    id: `favset-${Date.now()}`,
    createdAt: now,
    updatedAt: now,
  });
}

export function duplicateFavoriteSet(id: string): FavoriteSet | null {
  const source = getFavoriteSets().find((set) => set.id === id);
  if (!source) return null;
  return createFavoriteSet({
    name: `${source.name} Copy`,
    description: source.description,
    cardIds: [...source.cardIds],
    color: source.color,
  });
}

export function deleteFavoriteSet(id: string): boolean {
  const sets = getFavoriteSets();
  const next = sets.filter((set) => set.id !== id);
  if (next.length === sets.length) return false;
  writeJson(STORAGE_KEYS.favoriteSets, next);
  return true;
}

export function addCardToFavoriteSet(setId: string, cardId: string): FavoriteSet | null {
  const set = getFavoriteSets().find((entry) => entry.id === setId);
  if (!set) return null;
  if (!set.cardIds.includes(cardId)) {
    set.cardIds.push(cardId);
  }
  set.updatedAt = new Date().toISOString();
  return saveFavoriteSet(set);
}

export function removeCardFromFavoriteSet(setId: string, cardId: string): FavoriteSet | null {
  const set = getFavoriteSets().find((entry) => entry.id === setId);
  if (!set) return null;
  set.cardIds = set.cardIds.filter((entry) => entry !== cardId);
  set.updatedAt = new Date().toISOString();
  return saveFavoriteSet(set);
}

export function reorderFavoriteSetCards(setId: string, orderedIds: string[]): FavoriteSet | null {
  const set = getFavoriteSets().find((entry) => entry.id === setId);
  if (!set) return null;
  const existing = new Set(orderedIds);
  set.cardIds = [...orderedIds, ...set.cardIds.filter((id) => !existing.has(id))];
  set.updatedAt = new Date().toISOString();
  return saveFavoriteSet(set);
}

export function exportFavoriteSets(): string {
  return JSON.stringify(
    {
      version: 1,
      exportedAt: new Date().toISOString(),
      sets: getFavoriteSets(),
    },
    null,
    2,
  );
}

export function previewFavoriteSetImport(text: string, cards: LibraryCard[]): FavoriteSetImportPreview {
  const payload = JSON.parse(text) as { sets?: FavoriteSet[] };
  const incoming = Array.isArray(payload.sets) ? payload.sets.map(normalizeFavoriteSet) : [];
  const existing = getFavoriteSets();
  const cardIds = new Set(cards.map((card) => card.id));
  const conflicts: FavoriteSetConflict[] = [];
  const missingCardIds = new Set<string>();

  for (const set of incoming) {
    const sameId = existing.find((entry) => entry.id === set.id);
    const sameName = existing.find((entry) => entry.name.toLowerCase() === set.name.toLowerCase());
    if (sameId) conflicts.push({ incoming: set, existing: sameId, reason: 'id' });
    else if (sameName) conflicts.push({ incoming: set, existing: sameName, reason: 'name' });

    for (const cardId of set.cardIds) {
      if (!cardIds.has(cardId)) {
        missingCardIds.add(cardId);
      }
    }
  }

  return {
    incoming,
    conflicts,
    missingCardIds: Array.from(missingCardIds),
    warnings: missingCardIds.size > 0 ? ['Some imported card IDs do not exist on this device.'] : [],
  };
}

export function applyFavoriteSetImport(
  preview: FavoriteSetImportPreview,
  resolution: Record<string, FavoriteSetConflictChoice>,
  mode: 'merge' | 'replace',
): FavoriteSet[] {
  const next = mode === 'replace' ? [] : getFavoriteSets();

  for (const incoming of preview.incoming) {
    const conflict = preview.conflicts.find((entry) => entry.incoming.id === incoming.id || entry.incoming.name === incoming.name);
    const choice = conflict ? resolution[incoming.id] || 'skip' : 'replace';

    if (conflict) {
      if (choice === 'skip') continue;
      if (choice === 'rename') {
        next.unshift(
          normalizeFavoriteSet({
            ...incoming,
            id: `favset-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            name: `${incoming.name} Imported`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }),
        );
        continue;
      }
      const index = next.findIndex((set) => set.id === conflict.existing.id);
      if (index >= 0) {
        next[index] = normalizeFavoriteSet({
          ...incoming,
          id: conflict.existing.id,
          createdAt: conflict.existing.createdAt,
          updatedAt: new Date().toISOString(),
        });
      }
      continue;
    }

    next.unshift(
      normalizeFavoriteSet({
        ...incoming,
        id: incoming.id || `favset-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        createdAt: incoming.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }),
    );
  }

  writeJson(STORAGE_KEYS.favoriteSets, next);
  return next;
}

export function downloadFavoriteSets(): void {
  const blob = new Blob([exportFavoriteSets()], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `service-companion_favorite-sets_${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function getLibraryRecentSearches(): string[] {
  return readStringArray(STORAGE_KEYS.libraryRecentSearches);
}

export function recordLibraryRecentSearch(query: string): void {
  const trimmed = query.trim();
  if (trimmed.length < 2) return;
  const next = getLibraryRecentSearches().filter((entry) => entry.toLowerCase() !== trimmed.toLowerCase());
  next.unshift(trimmed);
  saveStringArray(STORAGE_KEYS.libraryRecentSearches, next, MAX_RECENTS);
}

export function clearLibraryRecentSearches(): void {
  writeJson(STORAGE_KEYS.libraryRecentSearches, []);
}

export function getSpotlightRecentSearches(): string[] {
  return readStringArray(STORAGE_KEYS.spotlightRecentSearches);
}

export function recordSpotlightRecentSearch(query: string): void {
  const trimmed = query.trim();
  if (trimmed.length < 2) return;
  const next = getSpotlightRecentSearches().filter((entry) => entry.toLowerCase() !== trimmed.toLowerCase());
  next.unshift(trimmed);
  saveStringArray(STORAGE_KEYS.spotlightRecentSearches, next, MAX_RECENTS);
}

export function clearSpotlightRecentSearches(): void {
  writeJson(STORAGE_KEYS.spotlightRecentSearches, []);
}

export function getFavoriteTopics(): string[] {
  return readStringArray(STORAGE_KEYS.favoriteTopics);
}

export function toggleFavoriteTopic(topicId: string): boolean {
  const topics = getFavoriteTopics();
  const index = topics.indexOf(topicId);
  if (index >= 0) {
    topics.splice(index, 1);
    saveStringArray(STORAGE_KEYS.favoriteTopics, topics, 50);
    return false;
  }
  topics.unshift(topicId);
  saveStringArray(STORAGE_KEYS.favoriteTopics, topics, 50);
  return true;
}
